import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import referenceMarkup from "./operationReference";
import { ReferenceDOM, OperationHeader, OperationFooter, OperationTheme, OperationContact } from "./OperationShell";
import OperationMap from "./OperationMap";
import useOperationTelemetry from "./useOperationTelemetry";
import { SOURCE, isFresh, compact, number, clock, countryName, windowData } from "./operationData";
import "./OperationShell.css";
import "./SecurityOperationsCenter.css";
import "./OperationOverrides.css";

const P = "operation-module__Zgd_Ea__";
const cls = name => `.${P}${name}`;
const ATTACK_TYPES = { tcp_port: "TCP-Port-Anfragen", udp_port: "UDP-Port-Anfragen", tcp_syn: "TCP SYN", udp_packet: "UDP-Pakete", xdp_udp: "XDP UDP", tcp_concurrent: "Gleichzeitige TCP-Verbindungen" };

export default function SecurityOperationsCenter() {
  const root = useRef(null);
  const [parts, setParts] = useState(null);
  const [range, setRange] = useState("24h");
  const [expanded, setExpanded] = useState(false);
  const [theme, setTheme] = useState(() => { try { return localStorage.getItem("operation-theme") === "light" ? "light" : "dark"; } catch (_) { return "dark"; } });
  const [now, setNow] = useState(Date.now());
  const feed = useOperationTelemetry();
  const data = feed.data;
  const fresh = isFresh(data, feed.available, now);
  const chartData = useMemo(() => windowData(data, range), [data, range]);
  const events = (data?.events || []).slice(0, expanded ? 20 : 5);

  useEffect(() => {
    const doc = document.documentElement, oldTitle = document.title, oldTheme = doc.dataset.theme;
    doc.classList.add("operation-document");
    document.title = "Cyber-Abwehr | RedWORK";
    const el = root.current;
    const map = el.querySelector(cls("map"));
    map.querySelector(":scope > svg")?.remove();
    setParts({ map, chart: el.querySelector(`${cls("chart")} > svg`), table: el.querySelector(`${cls("tableWrap")} tbody`), recent: el.querySelector(cls("mobileRecent")) });
    const change = event => { if (event.target.getAttribute("aria-label") === "Zeitraum des Diagramms") setRange(event.target.value); };
    const click = event => { if (event.target.closest(cls("allEvents")) || event.target.closest(`${cls("mobileRecent")} header button`)) setExpanded(value => !value); };
    el.addEventListener("change", change); el.addEventListener("click", click);
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(timer); el.removeEventListener("change", change); el.removeEventListener("click", click);
      doc.classList.remove("operation-document");
      if (oldTheme === undefined) delete doc.dataset.theme; else doc.dataset.theme = oldTheme;
      document.title = oldTitle;
    };
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    root.current.querySelector("main").dataset.operationTheme = theme;
    try { localStorage.setItem("operation-theme", theme); } catch (_) { /* Storage may be disabled. */ }
  }, [theme]);

  useEffect(() => {
    const el = root.current;
    const set = (selector, value) => { const node = el.querySelector(selector); if (node) node.textContent = value; };
    const metrics = [...el.querySelectorAll(cls("metric"))];
    const metricValues = [`${compact(data?.events?.[0]?.peakPps)} PPS`, `${compact(fresh ? data?.blockedPps : null)} PPS`, number(data?.attackCount), fresh && data?.protection?.xdp ? "Aktiv" : "—"];
    metrics.forEach((node, i) => {
      node.querySelector("strong").textContent = metricValues[i];
      if (node.hasAttribute("aria-label")) node.setAttribute("aria-label", `${node.querySelector(":scope > div > span").textContent}: ${metricValues[i]}`);
    });
    const live = el.querySelector(cls("live"));
    live.dataset.fresh = String(fresh);
    const liveText = [...live.childNodes].find(node => node.nodeType === 3);
    if (liveText) liveText.textContent = fresh ? "LIVE" : "DATEN AUSSTEHEND";
    live.title = "Öffentliche Live-Daten: MeoHost Bursa. Keine RedWORK-Netzwerkmessung.";
    set(`${cls("live")} span`, new Date(now).toLocaleDateString("de-CH", { timeZone: "Europe/Zurich", day: "2-digit", month: "short", year: "numeric" }));
    set(`${cls("live")} b`, clock(now));
    set(cls("mapNote"), `Global · ${compact(data?.blockedPackets)} blockierte Pakete · Letzte 24 Stunden`);
    set(`${cls("mobileMapNote")} strong`, compact(data?.blockedPackets));
    set(`${cls("mobilePackets")} strong`, compact(data?.blockedPackets));
    const mobileStatus = el.querySelector(cls("mobileMapState"));
    mobileStatus.dataset.confirmed = String(fresh && data?.protection?.xdp === true);
    const statusText = [...mobileStatus.childNodes].find(node => node.nodeType === 3);
    if (statusText) statusText.textContent = fresh && data?.protection?.xdp ? "Schutzsysteme aktiv" : "Aktueller Status ausstehend";
    set(`${cls("packetTotal")} strong`, compact(chartData?.blockedPackets));
    set(`${cls("packetTotal")} span`, `Blockierte Pakete · ${range === "1h" ? "Letzte Stunde" : "Letzte 24 Stunden"}`);
    const facts = [...el.querySelectorAll(`${cls("packetFacts")} > div b`)];
    [compact(chartData?.blockedPackets), number(chartData?.attackCount), "99,7 %"].forEach((value, i) => { if (facts[i]) facts[i].textContent = value; });
    set(`${cls("darkFacts")} > div:first-child b`, number(chartData?.attackCount));
    const protection = data?.protection;
    const pathsKnown = fresh && Number.isFinite(protection?.pathCount) && protection.pathCount > 0 && Number.isFinite(protection.activePathCount);
    const pathsActive = pathsKnown && protection.activePathCount === protection.pathCount;
    const rows = [...el.querySelectorAll(`[class*="${P}healthRow"]`)];
    const statuses = [fresh && protection?.xdp ? "Aktiv" : "—", fresh && protection?.xdp ? "Aktiv" : "—", pathsKnown ? `${protection.activePathCount}/${protection.pathCount}` : "—", pathsKnown ? pathsActive ? "Aktiv" : "Teilweise" : "—", fresh && protection?.agentReachable ? "In Betrieb" : "—"];
    rows.forEach((row, i) => { row.querySelector(":scope > span").textContent = statuses[i]; row.dataset.confirmed = String(statuses[i] !== "—"); });
    set(`${cls("health")} header p`, fresh && protection?.xdp ? "Die Systeme werden aktiv geschützt." : "Aktuelle Schutzdaten ausstehend.");
    if (pathsKnown && rows[3]) rows[3].querySelector("small").textContent = `${protection.activePathCount}/${protection.pathCount} Schutzverbindungen aktiv`;
    const healthFooter = el.querySelector(`${cls("health")} > p`);
    if (healthFooter) healthFooter.hidden = fresh;
    const empty = el.querySelector(`${cls("events")} ${cls("empty")}`);
    if (empty) empty.hidden = Boolean(data?.events?.length);
    const mobileEmpty = el.querySelector(cls("mobileEmpty"));
    if (mobileEmpty) mobileEmpty.hidden = Boolean(data?.events?.length);
    el.querySelectorAll(`${cls("allEvents")}, ${cls("mobileRecent")} header button`).forEach(button => { button.disabled = (data?.events?.length || 0) <= 5; const text = [...button.childNodes].find(node => node.nodeType === 3); if (text) text.textContent = expanded ? "Weniger anzeigen" : "Alle anzeigen"; });
    const series = chartData?.series || [];
    const peak = Math.max(1, ...series.filter(p => Number.isFinite(p.pps)).map(p => p.pps));
    const axis = el.querySelectorAll(`${cls("axis")} span`);
    [series.length ? compact(peak) : "—", series.length ? compact(peak / 2) : "—", "0"].forEach((value, i) => { if (axis[i]) axis[i].textContent = value; });
    const from = Date.parse(chartData?.window?.from), to = Date.parse(chartData?.window?.to);
    el.querySelectorAll(`${cls("times")} span`).forEach((node, i) => { node.textContent = Number.isFinite(from) && Number.isFinite(to) ? new Date(from + (to - from) * i / 5).toLocaleTimeString("de-CH", { timeZone: "Europe/Zurich", hour: "2-digit", minute: "2-digit" }) : "—"; });
    const sparkValues = (data?.series || []).slice(-24).map(p => p.pps).filter(Number.isFinite);
    const max = Math.max(1, ...sparkValues);
    const spark = sparkValues.map((v, i) => `${i ? "L" : "M"} ${i * 120 / Math.max(1, sparkValues.length - 1)} ${40 - v / max * 35}`).join(" ");
    const mobileSpark = el.querySelector(`${cls("mobilePackets")} > svg path`);
    if (mobileSpark) mobileSpark.setAttribute("d", spark);
    metrics[1]?.querySelectorAll("svg path").forEach(path => { if (path.closest(`.${P}metricIcon`)) return; path.setAttribute("d", spark); });
  }, [data, fresh, now, chartData, range, expanded]);

  const series = chartData?.series || [];
  const maxPps = Math.max(1, ...series.filter(p => Number.isFinite(p.pps)).map(p => p.pps));
  const start = Date.parse(chartData?.window?.from), end = Date.parse(chartData?.window?.to);
  return <div className="operation-site site-shell-module__o42stq__site" data-theme={theme}>
    <OperationHeader />
    <ReferenceDOM ref={root} markup={referenceMarkup} />
    <OperationMap target={parts?.map} data={data} fresh={fresh} theme={theme} />
    {parts?.chart && createPortal(<g fill="#22d974" data-live-chart="true">{series.map((sample, i) => {
      if (!Number.isFinite(sample.pps) || !Number.isFinite(start) || !(end > start)) return null;
      const x = Math.max(0, Math.min(599, (sample.t - start) / (end - start) * 600));
      const height = Math.max(.4, sample.pps / maxPps * 110);
      return <rect key={sample.t ?? i} x={x} y={120 - height} width={Math.max(.7, Math.min(5, 600 / Math.max(1, series.length) * .55))} height={height}><title>{clock(sample.t)} · {number(sample.pps)} PPS</title></rect>;
    })}</g>, parts.chart)}
    {parts?.table && createPortal(<>{events.map((event, i) => <tr key={event.at || i}>
      <td>{clock(event.at)}</td><td>{event.countryCode ? countryName(event.countryCode) : "—"}</td>
      <td>{(event.attackTypes || []).map(code => ATTACK_TYPES[code] || code).join(" / ") || "—"}</td>
      <td title="Systemweite blockierte Spitzen-PPS">{compact(event.peakPps)}</td>
      <td><span className={P + "eventStatus"} data-confirmed={event.ended}>{event.ended ? "Blockiert" : "Ende unbestätigt"}</span></td>
    </tr>)}</>, parts.table)}
    {parts?.recent && events.length > 0 && createPortal(<div className="operation-mobile-events">{events.map((event, i) => <div key={event.at || i}><strong>{clock(event.at)}</strong><span>{compact(event.peakPps)} PPS</span><small>{event.ended ? "Blockiert" : "Ende unbestätigt"} · {number(event.packets)} blockierte Pakete</small></div>)}</div>, parts.recent)}
    <p className="operation-source">Live-Daten: <a href={SOURCE.url} target="_blank" rel="noreferrer">{SOURCE.network}</a> · Externes Referenznetzwerk, keine RedWORK-Netzwerkmessung. Schutzquote gemäss Referenzangabe.</p>
    <OperationFooter />
    <OperationTheme theme={theme} onChange={setTheme} />
    <OperationContact />
  </div>;
}
